import React, { useState } from 'react';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import ContextMenu, {
	ContextMenuParams,
} from '../../../../Components/ContextMenu/ContextMenu';
import CheckLastIssueOfMedicationModal from '../../../EPrescription/Components/CheckLastIssueOfMedication/CheckLastIssueOfMedicationModal';
// @ts-ignore
import styles from './CasePermanentMedicationOptionsContextMenu.module.scss';
import useTranslation from '../../../../Hooks/useTranslation';
import SearchMedicamentRecordModal from '../../../../Components/eRecept/SearchMedicamentRecord/SearchMedicamentRecordModal';
import ElectronicPrescriptionModal from '../../../Prescription/Components/ElectronicPrescriptionModal';
import { useLazyUserMedicationLastedQuery } from '../../../../graphql/graphql';
import { IPermanentMedicament } from '../../../EPrescription/Components/PermanentMedication/PernamentMedicationItem';
import { useAppContext } from '../../../../Services/AppContext';
import Loading from '../../../../Components/Loading';
import { prepareDefaultDataFromPermanentMedication } from '../../../EPrescription/Services/permanentMedication.service';
import CasePermanentMedicationDeclineModal from '../CasePermanentMedicationDecline/CasePermanentMedicationDeclineModal';
import Button from '../../../../Components/Button/Button';

export enum PermanentMedicationContextMenuTriggerType {
	LINK = 'link',
	BUTTON = 'button',
}

interface Props {
	patientId: string;
	caseId: string;
	medications: string[];
	reloadCaseList: () => void;
	triggerType?: PermanentMedicationContextMenuTriggerType;
}

const CasePermanentMedicationOptionsContextMenu: React.FC<Props> = ({
	patientId,
	caseId,
	medications,
	reloadCaseList,
	triggerType = PermanentMedicationContextMenuTriggerType.LINK,
}) => {
	const [getPermanentMedicationQuery] = useLazyUserMedicationLastedQuery();
	const { t } = useTranslation();
	const { workplaceId } = useAppContext();
	const [permanentMedication, setPermanentMedication] = React.useState<
		IPermanentMedicament[]
	>([]);
	const [contextMenuProps, setContextMenuProps] = useState<ContextMenuParams>({
		isOpen: false,
	});
	const [isCheckOfLastIssueOpen, setIsCheckOfLastIssueOpen] = useState(false);
	const [isMedicalRecordOpen, setIsMedicalRecordOpen] = useState(false);
	const [isIssueOpen, setIsIssueOpen] = useState(false);
	const [isDeclineOpen, setIsDeclineOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [defaultValues, setDefaultValues] = useState<object | undefined>(
		undefined,
	);

	const handleLoadPatientsPermanentMedication = async () => {
		setIsLoading(true);
		const temp: IPermanentMedicament[] = [];
		const data = await getPermanentMedicationQuery({
			where: { userId: patientId, workplaceId },
		});
		data?.data?.userMedicationLasted?.data.forEach(i => {
			if (medications.some(item => item === i.id)) {
				temp.push(i);
			}
		});
		setPermanentMedication(temp);
		setIsLoading(false);
	};

	const openContext = async (event: any) => {
		await handleLoadPatientsPermanentMedication();
		setContextMenuProps({
			isOpen: true,
			clientX: event.clientX,
			clientY: event.clientY,
		});
	};

	const openCheckOfLastIssue = () => {
		setIsCheckOfLastIssueOpen(true);
		setContextMenuProps({ isOpen: false });
	};

	const openMedicalRecord = () => {
		setIsMedicalRecordOpen(true);
		setContextMenuProps({ isOpen: false });
	};

	const openIssue = () => {
		setDefaultValues(
			prepareDefaultDataFromPermanentMedication(permanentMedication),
		);
		setIsIssueOpen(true);
		setContextMenuProps({ isOpen: false });
	};

	const openDecline = () => {
		setIsDeclineOpen(true);
		setContextMenuProps({ isOpen: false });
	};

	const onDeclineRequest = () => {
		setIsDeclineOpen(false);
		reloadCaseList();
	};

	const onIssueFromCheck = (medications: IPermanentMedicament[]) => {
		setDefaultValues(prepareDefaultDataFromPermanentMedication(medications));
		setIsCheckOfLastIssueOpen(false);
		setIsIssueOpen(true);
		setContextMenuProps({ isOpen: false });
	};

	const buttons: React.ReactElement = (
		<div className={styles.wrapper}>
			<LinkButton
				title='component.casePermanentMedicationOptionsContextMenu.checkLastIssue'
				theme={Theme.BLUE}
				onClick={openCheckOfLastIssue}
			/>
			<LinkButton
				title='component.casePermanentMedicationOptionsContextMenu.medicalRecord'
				theme={Theme.BLUE}
				onClick={openMedicalRecord}
			/>
			<LinkButton
				title='component.casePermanentMedicationOptionsContextMenu.issue'
				theme={Theme.BLUE}
				onClick={openIssue}
			/>
			<LinkButton
				title='component.casePermanentMedicationOptionsContextMenu.decline'
				theme={Theme.RED}
				onClick={openDecline}
			/>
		</div>
	);

	return (
		<>
			{triggerType === PermanentMedicationContextMenuTriggerType.LINK && (
				<div className={styles.button} onClick={openContext}>
					{t('base.options2')}
				</div>
			)}
			{triggerType === PermanentMedicationContextMenuTriggerType.BUTTON && (
				<div onClick={openContext}>
					<Button title='base.options2' theme={Theme.GREEN} small />
				</div>
			)}
			<ContextMenu
				params={contextMenuProps}
				openInModalRoot={true}
				customRootName='context-menu-root'
				onContextClose={() => {
					setContextMenuProps({ isOpen: false });
				}}>
				{isLoading ? <Loading /> : buttons}
			</ContextMenu>
			{isCheckOfLastIssueOpen && (
				<CheckLastIssueOfMedicationModal
					isOpen
					onClose={() => setIsCheckOfLastIssueOpen(false)}
					medications={permanentMedication}
					patientId={patientId}
					onIssue={onIssueFromCheck}
				/>
			)}
			{isMedicalRecordOpen && (
				<SearchMedicamentRecordModal
					isOpen
					onClose={() => setIsMedicalRecordOpen(false)}
					patientId={patientId}
				/>
			)}
			{isIssueOpen && (
				<ElectronicPrescriptionModal
					opened
					onClose={() => setIsIssueOpen(false)}
					patientId={patientId}
					defaultValues={defaultValues}
				/>
			)}
			{isDeclineOpen && (
				<CasePermanentMedicationDeclineModal
					isOpen
					onClose={() => setIsDeclineOpen(false)}
					caseId={caseId}
					onSuccess={onDeclineRequest}
				/>
			)}
		</>
	);
};

export default CasePermanentMedicationOptionsContextMenu;
